import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { useMusicVideoEditor } from '../../MusicVideoEditorContext';
import { footageMessages } from '../../messages/footageMessages';
import { ShowcaseLockedTab } from '../../showcase/ShowcaseLockedTab';
import { showcaseMessages } from '../../showcase/showcaseMessages';
import { AiTab } from './AiTab';
import { BankTab } from './BankTab';
import { UploadTab } from './UploadTab';
import { YoursTab } from './YoursTab';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../../MusicVideoEditor.module.scss';
import showcaseStyles from '../../showcase/MusicVideoShowcase.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The tabs of the Footage panel (design h1: Bank, AI, Yours, Upload in every frame). */
export const FOOTAGE_TABS = ['bank', 'ai', 'yours', 'upload'] as const;
export type FootageTab = (typeof FOOTAGE_TABS)[number];

const TAB_MESSAGES = {
  bank: footageMessages.tabBank,
  ai: footageMessages.tabAi,
  yours: footageMessages.tabYours,
  upload: footageMessages.tabUpload,
} as const;

/** The small lock of a tab that needs an account (design c1, c3). */
const TabLock: React.FC<{ label: string }> = ({ label }) => (
  <svg
    className={showcaseStyles.tabLock}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    role="img"
    aria-label={label}
  >
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

/**
 * The Footage panel (design b1, c1, c2, h1): the four tabs and the drag hint. In showcase mode the AI,
 * Yours and Upload tabs carry a lock and show the sign-up card of design c3.
 */
const FootagePanelComponent: React.FC<{ hideTitle?: boolean }> = ({ hideTitle = false }) => {
  const intl = useIntl();
  const { mode } = useMusicVideoEditor();
  const isShowcase = mode === 'showcase';
  const [tab, setTab] = useState<FootageTab>('bank');
  const showBank = () => setTab('bank');
  return (
    <>
      {!hideTitle && <h3 className={styles.paneTitle}>{intl.formatMessage(footageMessages.title)}</h3>}
      <div className={styles.tabs} role="tablist">
        {FOOTAGE_TABS.map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={item === tab}
            className={clsx(styles.segment, item === tab && styles.segmentOn)}
            onClick={() => setTab(item)}
          >
            {isShowcase && item !== 'bank' ? (
              <span className={showcaseStyles.tabLabel}>
                {intl.formatMessage(TAB_MESSAGES[item])}
                <TabLock label={intl.formatMessage(showcaseMessages.locked)} />
              </span>
            ) : (
              intl.formatMessage(TAB_MESSAGES[item])
            )}
          </button>
        ))}
      </div>
      {tab === 'bank' && <BankTab onShowYours={() => setTab('yours')} />}
      {isShowcase && tab !== 'bank' && <ShowcaseLockedTab key={tab} tab={tab} onBackToBank={showBank} />}
      {!isShowcase && tab === 'ai' && <AiTab />}
      {!isShowcase && tab === 'yours' && <YoursTab />}
      {!isShowcase && tab === 'upload' && <UploadTab />}
      {tab === 'bank' && (
        <>
          <span className={styles.grow} />
          <p className={styles.hint}>{intl.formatMessage(footageMessages.panelHint)}</p>
        </>
      )}
    </>
  );
};

export const FootagePanel = TypedMemo(FootagePanelComponent);
